import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck, Landmark, Handshake, CheckCircle2, Mail, MapPin,
  ArrowRight, Menu, X,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const NAV = [
  { label: "About Us", id: "about", testid: "nav-about-link" },
  { label: "Meet the Team", id: "team", testid: "nav-team-link" },
  { label: "Services", id: "services", testid: "nav-services-link" },
  { label: "Locations", id: "locations", testid: "nav-locations-link" },
  { label: "Contact Us", id: "contact", testid: "nav-contact-link" },
];

const TEAM = [
  { name: "Prashant Kesarkar", role: "Founding Partner", img: "/team-prashant.jpg" },
  { name: "Sonali Patil", role: "Head of Conveyance", img: "/team-sonali.jpg" },
  { name: "Akshay Kriplani", role: "Head Accountant", img: "/team-akshay.jpg" },
  { name: "Property Management Team", role: "", img: "/team-group.jpg" },
];

const SERVICES = [
  { title: "Sale", accent: "emerald", items: ["Property Due Diligence", "Market Valuation", "Supporting Negotiations", "Agreements and Sale Deed Drafting", "Documentation Review", "Legal Registrations", "Tax Implications", "Handover Facilitation"] },
  { title: "Purchase", accent: "sky", items: ["Property Due Diligence", "Market Valuation", "Feasibility Reports", "Search Reports", "Supporting Negotiations", "Agreements and Sale Deed Drafting", "Documentation Review", "Legal Registrations", "Tax Implications", "Handover Facilitation", "Tenancy Prospects"] },
  { title: "Tenancy", accent: "amber", items: ["Tenant Search", "Tenant Due Diligence", "Supporting Negotiations", "Tenancy Agreement", "Documentation Review", "Legal Registrations", "Tax Implications", "Handover Facilitation", "Police Verification"] },
  { title: "Maintenance", accent: "rose", items: ["General Maintenance", "Tenancy Inspections", "Tenancy renewals", "End of Tenancy cleaning", "Rental advertisements", "Keyholding and viewings"] },
];

const ACCENTS = {
  emerald: { head: "bg-emerald-600", border: "border-emerald-200", icon: "text-emerald-600" },
  sky: { head: "bg-sky-600", border: "border-sky-200", icon: "text-sky-600" },
  amber: { head: "bg-amber-500", border: "border-amber-200", icon: "text-amber-500" },
  rose: { head: "bg-rose-600", border: "border-rose-200", icon: "text-rose-600" },
};

const PILLARS = [
  { icon: Landmark, title: "Management", desc: "End-to-end oversight of tenancies, valuations and portfolio performance." },
  { icon: Handshake, title: "Conveyance", desc: "Seamless sale and purchase pipelines with meticulous legal coordination." },
  { icon: ShieldCheck, title: "Maintenance", desc: "Proactive maintenance and asset care for the properties that matter most." },
];

const HERO_IMG = "/hero-india.jpg";

const BTN = "bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-all duration-200 shadow-sm active:scale-[0.98]";

const Section = ({ children, id, className = "" }) => (
  <section id={id} className={`scroll-mt-24 px-6 md:px-12 lg:px-20 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <span className="text-xs uppercase tracking-[0.25em] font-mono text-emerald-700 font-semibold">{children}</span>
);

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "General Enquiry", message: "" });
  const [sending, setSending] = useState(false);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent.");
      setForm({ name: "", email: "", phone: "", topic: "General Enquiry", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const inputCls = "mt-1.5 w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 overflow-x-hidden">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 h-16 flex items-center justify-between">
          <button data-testid="nav-home-link" onClick={() => scrollTo("hero")} className="flex items-center gap-3 group">
            <img src="/va-logo-orange.png" alt="VA Property Care" className="h-12 w-12 object-contain shrink-0" />
            <span className="font-display text-2xl font-bold tracking-tight text-slate-900">VA Property Care</span>
          </button>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <button key={n.id} data-testid={n.testid} onClick={() => scrollTo(n.id)}
                className="text-sm font-bold text-slate-800 hover:text-emerald-700 transition-colors">{n.label}</button>
            ))}
            <Link to="/login" data-testid="nav-client-login-button" className={`${BTN} text-sm px-5 py-2.5 rounded-lg`}>
              Client Login
            </Link>
          </nav>
          <button className="md:hidden text-slate-700" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
            {menuOpen ? <X /> : <Menu />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 px-6 py-4 flex flex-col gap-4">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => scrollTo(n.id)} className="text-left font-bold text-slate-800 py-1">{n.label}</button>
            ))}
            <Link to="/login" className={`${BTN} text-center px-5 py-2.5 rounded-lg`}>Client Login</Link>
          </div>
        )}
      </header>

      {/* HERO */}
      <div id="hero" className="relative min-h-screen flex items-center pt-16">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Luxury apartments in India" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/5" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-white via-emerald-50/60 to-transparent" />
        </div>
        <Section className="relative py-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-2xl bg-slate-900/45 backdrop-blur-md rounded-2xl p-8 sm:p-10 shadow-2xl ring-1 ring-white/10">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] font-display text-emerald-400">
              Your Property.<br />Our Priority.
            </h1>
            <div className="mt-6 max-w-2xl space-y-3 text-white" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.55)" }}>
              <p className="text-lg leading-relaxed font-medium">
                VA Property Care provides comprehensive property management and transaction support for overseas clients with property interests in India{" "}
                <button type="button" onClick={() => scrollTo("locations")} data-testid="hero-locations-link"
                  className="text-sm underline decoration-emerald-300 underline-offset-2 text-emerald-200 hover:text-white font-semibold transition-colors">see serviced locations</button>
              </p>
              <p className="text-base leading-relaxed text-white/95">
                From property sales and purchases to tenancy management, our dedicated team oversees every stage of the process on your behalf, ensuring a seamless, transparent and professionally managed experience.
              </p>
              <p className="text-base leading-relaxed text-white/95">
                Through our secure online portal, you have complete visibility of your property matters, including key documents, actions, updates and progress.
              </p>
            </div>
            <p className="mt-4 text-base font-semibold text-emerald-300 max-w-2xl">
              Book a 30 min free consultation to discuss your requirements.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button onClick={() => scrollTo("contact")} data-testid="hero-advisor-btn" className={`${BTN} inline-flex items-center gap-2 px-6 py-3.5 rounded-lg`}>
                Speak to an Advisor <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        </Section>
      </div>

      {/* ABOUT */}
      <Section id="about" className="py-24">
        <div className="max-w-2xl">
          <Eyebrow>About Us</Eyebrow>
          <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-display text-slate-900">
            A dedicated custodian for your properties in India
          </h2>
          <p className="mt-5 text-base text-slate-600 leading-relaxed">
            We provide clients with a single, trusted partner across every dimension of property ownership. Our transparent
            and trusted approach blends meticulous care with disciplined management.
          </p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
              className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-emerald-300">
              <span className="h-12 w-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <p.icon className="h-6 w-6 text-emerald-700" />
              </span>
              <h3 className="mt-5 text-xl font-semibold text-slate-900">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* TEAM */}
      <Section id="team" className="py-24">
        <Eyebrow>Meet the Team</Eyebrow>
        <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-display text-slate-900">The partners behind the practice.</h2>
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl">
          {TEAM.map((m, i) => (
            <motion.div key={m.name} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-emerald-300">
              <div className="aspect-square overflow-hidden">
                <img src={m.img} alt={m.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="py-4 pl-5 sm:pl-[18%] pr-3" data-testid="team-member-info">
                <h3 className="font-semibold text-slate-900 text-sm sm:text-base">{m.name}</h3>
                {m.role && <p className="text-sm text-emerald-700 font-mono mt-1">{m.role}</p>}
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* SERVICES */}
      <Section id="services" className="py-24">
        <Eyebrow>Services</Eyebrow>
        <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-display text-slate-900">How we support you at every stage.</h2>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {SERVICES.map((s, i) => {
            const c = ACCENTS[s.accent];
            return (
              <motion.div key={s.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`rounded-2xl overflow-hidden bg-white border ${c.border} shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg`}>
                <div className={`px-6 py-5 ${c.head}`}>
                  <h3 className="text-xl font-bold text-white font-display">{s.title}</h3>
                </div>
                <ul className="p-6 space-y-3" data-testid={`services-list-${s.title.toLowerCase()}`}>
                  {s.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-sm text-slate-700">
                      <CheckCircle2 className={`h-5 w-5 ${c.icon} shrink-0 mt-0.5`} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </Section>

      {/* LOCATIONS */}
      <Section id="locations" className="py-24 bg-white">
        <Eyebrow>Locations</Eyebrow>
        <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-display text-slate-900">Where we work.</h2>
        <p className="mt-4 text-base text-slate-600 max-w-2xl">We look after overseas clients across the UK while managing their properties on the ground in India.</p>
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="rounded-2xl overflow-hidden bg-slate-50 border border-slate-200 shadow-sm" data-testid="locations-client-card">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900 font-display">Client locations</h3>
            </div>
            <img src="/uk-map.jpg" alt="UK client locations map" className="w-full h-auto" data-testid="locations-uk-map" />
          </div>
          <div className="rounded-2xl overflow-hidden bg-slate-50 border border-slate-200 shadow-sm" data-testid="locations-india-card">
            <div className="px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900 font-display">Serviced India Locations</h3>
            </div>
            <img src="/india-map.jpg" alt="Serviced India locations map — Mumbai, Pune and Kolhapur" className="w-full h-auto" data-testid="locations-india-map" />
          </div>
        </div>
      </Section>

      {/* CONTACT */}
      <Section id="contact" className="py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <Eyebrow>Contact Us</Eyebrow>
            <h2 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-display text-slate-900">Begin a conversation.</h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Whether you're considering a sale, acquisition or full-service management, our advisors are ready to help.
            </p>
            <div className="mt-10 space-y-5">
              {[[Mail, "info@vapropertycare.com"], [MapPin, "Suite 52, 15 Commercial Road, Swindon, SN5 7EP"]].map(([Icon, v]) => (
                <div key={v} className="flex items-center gap-4">
                  <span className="h-11 w-11 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-emerald-700" />
                  </span>
                  <span className="text-slate-700">{v}</span>
                </div>
              ))}
            </div>
          </div>
          <form onSubmit={submitContact} data-testid="contact-form"
            className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-sm text-slate-600">Full name</label>
                <input required data-testid="contact-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="text-sm text-slate-600">Email</label>
                <input required type="email" data-testid="contact-email-input" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-600">Contact number</label>
              <input type="tel" data-testid="contact-phone-input" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9+ ]/g, "").slice(0, 20) })} inputMode="tel" className={inputCls} placeholder="e.g. +91 98765 43210" />
            </div>
            <div>
              <label className="text-sm text-slate-600">Topic</label>
              <select data-testid="contact-topic-select" value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })} className={inputCls}>
                {["General Enquiry", "Sale", "Purchase", "Tenancy", "Maintenance"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm text-slate-600">Message</label>
              <textarea required rows={4} data-testid="contact-message-input" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} />
            </div>
            <button type="submit" disabled={sending} data-testid="contact-submit-button"
              className={`${BTN} w-full px-6 py-3.5 rounded-lg disabled:opacity-60`}>
              {sending ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white px-6 md:px-12 lg:px-20 py-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/va-logo-orange.png" alt="VA Property Care" className="h-9 w-9 object-contain" />
            <span className="font-display font-bold text-lg text-slate-900">VA Property Care</span>
          </div>
          <p className="text-sm text-slate-500">© {new Date().getFullYear()} VA Property Care. All rights reserved.</p>
          <Link to="/login" className="text-sm text-emerald-700 hover:text-emerald-800 font-medium">Client Portal →</Link>
        </div>
      </footer>
    </div>
  );
}
